import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Loader2, Lock, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlbumSharePanel } from '@/components/AlbumSharePanel';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useToast } from '@/hooks/useToast';
import { ALBUM_KIND, albumHue, albumLink, manifestTags, saveAlbumKey } from '@/lib/album';
import { generateAlbumId, generateMasterKeyB64u, generateSaltHex } from '@/lib/albumCrypto';

interface CreateAlbumDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CreatedAlbum {
  pubkey: string;
  d: string;
  name: string;
  link: string;
  coverUrl?: string;
}

export function CreateAlbumDialog({ open, onOpenChange }: CreateAlbumDialogProps) {
  const { user } = useCurrentUser();
  const { mutateAsync: publish } = useNostrPublish();
  const { mutateAsync: uploadFile } = useUploadFile();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<CreatedAlbum | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function reset() {
    setName('');
    setCoverFile(null);
    setBusy(false);
    setCreated(null);
  }

  async function create() {
    if (!user) return;
    setBusy(true);
    try {
      const masterKey = generateMasterKeyB64u();
      const d = generateAlbumId();
      const salt = generateSaltHex();

      // Back up the master key on the manifest itself, NIP-44-encrypted to the
      // owner, so albums can be reopened on any device.
      let encryptedKey: string | undefined;
      if (user.signer.nip44) {
        encryptedKey = await user.signer.nip44.encrypt(user.pubkey, masterKey);
      } else {
        toast({
          title: 'Key backup unavailable',
          description: 'Your signer does not support NIP-44. The album key is only stored in this browser — keep your link safe.',
        });
      }

      let coverUrl: string | undefined;
      if (coverFile) {
        const tags = await uploadFile(coverFile);
        coverUrl = tags.find(([n]) => n === 'url')?.[1];
      }

      await publish({
        kind: ALBUM_KIND,
        content: '',
        tags: manifestTags({ d, title: name.trim(), salt, cover: coverUrl, encryptedKey, files: [], subs: [] }),
      });

      saveAlbumKey(user.pubkey, d, masterKey);
      const link = albumLink(window.location.origin, user.pubkey, d, masterKey);
      setCreated({ pubkey: user.pubkey, d, name: name.trim(), link, coverUrl });
      queryClient.invalidateQueries({ queryKey: ['my-albums'] });
    } catch (e) {
      console.error(e);
      toast({ title: 'Could not create album', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="sm:max-w-md">
        {created ? (
          <>
            <DialogHeader>
              <DialogTitle>Album created</DialogTitle>
              <DialogDescription>
                Save this link — it is the only way to open the album. The key is not stored anywhere else.
              </DialogDescription>
            </DialogHeader>
            <AlbumSharePanel
              link={created.link}
              name={created.name}
              albumId={created.d}
              coverUrl={created.coverUrl}
              hue={albumHue(created.d)}
            />
            <Button className="w-full" onClick={() => navigate(created.link.replace(window.location.origin, ''))}>
              Open album
            </Button>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="size-4" /> New encrypted album
              </DialogTitle>
              <DialogDescription>
                A fresh encryption key is generated on your device and baked into the share link.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="album-name">Album name (optional)</Label>
                <Input
                  id="album-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Summer 2026"
                  maxLength={80}
                />
              </div>
              <div className="space-y-2">
                <Label>Cover image (optional, public)</Label>
                {coverFile ? (
                  <div className="flex items-center gap-3 rounded-lg border p-2">
                    <img
                      src={URL.createObjectURL(coverFile)}
                      alt="Cover preview"
                      className="size-12 rounded-md object-cover"
                    />
                    <span className="min-w-0 flex-1 truncate text-sm">{coverFile.name}</span>
                    <Button variant="ghost" size="icon" onClick={() => setCoverFile(null)} aria-label="Remove cover">
                      <X className="size-4" />
                    </Button>
                  </div>
                ) : (
                  <Button variant="outline" className="w-full" onClick={() => fileRef.current?.click()}>
                    <ImagePlus className="size-4" /> Choose cover image
                  </Button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setCoverFile(e.target.files?.[0] ?? null)}
                />
                <p className="text-xs text-muted-foreground">
                  The cover is the only unencrypted image — everything else is end-to-end encrypted.
                </p>
              </div>
              <Button className="w-full" onClick={create} disabled={busy || !user}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : <Lock className="size-4" />}
                Create album
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
